import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faDollarSign,
  faChartLine,
  faBullseye,
  faCheckCircle,
  faSync,
  faExclamationTriangle,
  faInfoCircle,
  faChevronDown,
  faChevronUp,
  faTrophy,
  faStar,
  faCrown,
  faMedal,
  faRotate
} from '@fortawesome/free-solid-svg-icons';

interface SavingsData {
  baselineSpend: number;
  baselineMonth?: string; // e.g., "Sep 2025"
  baselineCapturedAt?: string;
  potentialSavings: number;
  achievedSavings: number;
  currentMonthSpend: number;
  savingsPotential: {
    x86ToArm: number;
    savingsPlans: number;
    reservedInstances: number;
    spotInstances: number;
    rightsizing: number;
    storageOptimization: number;
    scheduling: number;
    preApprovedDiscounts: number;
    credits: number;
    other: number;
  };
  savingsAchieved: {
    x86ToArm: number;
    savingsPlans: number;
    reservedInstances: number;
    spotInstances: number;
    rightsizing: number;
    storageOptimization: number;
    scheduling: number;
    preApprovedDiscounts: number;
    credits: number;
    other: number;
  };
  kpis: {
    realSavingsPercent: number;
    potentialSavingsPercent: number;
    remainingPotential: number;
    currentMonthSavings: number;
  };
}

export const SavingsDashboard: React.FC = () => {
  const [savingsData, setSavingsData] = useState<SavingsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [initialized, setInitialized] = useState(false);
  const [showBreakdown, setShowBreakdown] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isResetting, setIsResetting] = useState(false);

  const initializeBaseline = async () => {
    try {
      setIsInitializing(true);
      setError(null);

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/savings/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });

      const result = await response.json();

      if (result.success) {
        console.log('✅ Baseline initialized successfully');
        // Fetch the savings data to show the initialized state
        await fetchSavingsData();
      } else {
        setError(result.error || 'Failed to initialize baseline');
      }
    } catch (err: any) {
      console.error('Error initializing baseline:', err);
      setError(err.message);
    } finally {
      setIsInitializing(false);
    }
  };

  const resetAndRecalculate = async () => {
    try {
      setIsResetting(true);
      setError(null);

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      // Step 1: Reset savings tracking
      console.log('🗑️  Resetting savings tracking...');
      const resetResponse = await fetch(`${baseUrl}/api/savings/reset`, {
        method: 'DELETE',
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      if (!resetResponse.ok) {
        throw new Error('Failed to reset savings tracking');
      }

      // Step 2: Reinitialize baseline
      console.log('📊 Reinitializing baseline...');
      const initResponse = await fetch(`${baseUrl}/api/savings/initialize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });

      const initResult = await initResponse.json();
      if (!initResult.success) {
        throw new Error(initResult.error || 'Failed to initialize baseline');
      }

      // Step 3: Recalculate potential savings from scan
      console.log('🔄 Recalculating potential savings...');
      const scanResponse = await fetch(`${baseUrl}/api/savings/scan-and-calculate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token && { 'Authorization': `Bearer ${token}` })
        },
        body: JSON.stringify({})
      });

      if (scanResponse.ok) {
        console.log('✅ Savings tracking reset and recalculated with new business rules');
      }

      // Refresh the data
      await fetchSavingsData();
    } catch (err: any) {
      console.error('Error resetting savings tracking:', err);
      setError(err.message);
    } finally {
      setIsResetting(false);
    }
  };

  const fetchSavingsData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const baseUrl = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';
      const token = localStorage.getItem('auth_token');

      const response = await fetch(`${baseUrl}/api/savings/dashboard`, {
        headers: {
          ...(token && { 'Authorization': `Bearer ${token}` })
        }
      });

      const result = await response.json();

      if (result.success) {
        if (result.initialized) {
          setSavingsData(result.data);
          setInitialized(true);
        } else {
          setInitialized(false);
        }
      } else {
        setError(result.error || 'Failed to fetch savings data');
      }
    } catch (err: any) {
      console.error('Error fetching savings data:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchSavingsData();
  }, []);

  const getCategoryLabel = (category: string) => {
    const labels: Record<string, string> = {
      x86ToArm: 'x86→ARM Migration',
      savingsPlans: 'Savings Plans',
      reservedInstances: 'Reserved Instances',
      spotInstances: 'Spot Instances',
      rightsizing: 'Right-sizing',
      storageOptimization: 'Storage Optimization',
      scheduling: 'Scheduling',
      preApprovedDiscounts: 'DevOpsX Discounts',
      credits: 'Credits',
      other: 'Other'
    };
    return labels[category] || category;
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      x86ToArm: 'bg-purple-100 text-purple-700 border-purple-200',
      savingsPlans: 'bg-blue-100 text-blue-700 border-blue-200',
      reservedInstances: 'bg-indigo-100 text-indigo-700 border-indigo-200',
      spotInstances: 'bg-green-100 text-green-700 border-green-200',
      rightsizing: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      storageOptimization: 'bg-pink-100 text-pink-700 border-pink-200',
      scheduling: 'bg-orange-100 text-orange-700 border-orange-200',
      preApprovedDiscounts: 'bg-teal-100 text-teal-700 border-teal-200',
      credits: 'bg-cyan-100 text-cyan-700 border-cyan-200',
      other: 'bg-gray-100 text-gray-700 border-gray-200'
    };
    return colors[category] || colors.other;
  };

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-300 rounded p-3 animate-pulse font-mono">
        <div className="h-6 bg-gray-200 rounded w-64 mb-3"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded"></div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-3 mb-3 font-mono">
        <div className="flex items-center space-x-3">
          <FontAwesomeIcon icon={faExclamationTriangle} className="text-red-600" />
          <div>
            <div className="text-xs font-bold text-red-900">Failed to load savings tracking</div>
            <div className="text-xs text-red-700 mt-1">{error}</div>
          </div>
          <button
            onClick={fetchSavingsData}
            className="ml-auto px-3 py-1.5 bg-white border border-red-300 text-red-700 rounded text-xs font-medium hover:bg-red-50"
          >
            <FontAwesomeIcon icon={faSync} className="mr-1" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!initialized || !savingsData) {
    return (
      <div className="bg-white border border-gray-300 rounded p-3 mb-3 font-mono">
        <div className="flex items-start space-x-3">
          <FontAwesomeIcon icon={faInfoCircle} className="text-blue-600 text-sm mt-0.5" />
          <div className="flex-1">
            <h3 className="text-sm font-bold text-gray-900 mb-1">Savings Tracking Not Initialized</h3>
            <p className="text-xs text-gray-600 mb-2">
              Click "Initialize Now" to capture your current AWS spend as the baseline for tracking savings.
            </p>
            <div className="text-xs text-gray-700 bg-gray-50 border border-gray-300 rounded p-2 mb-3">
              <strong>How it works:</strong>
              <ol className="list-decimal list-inside mt-1 space-y-0.5">
                <li>Captures your current monthly spend as the baseline</li>
                <li>Calculates potential savings from recommendations</li>
                <li>Tracks achieved savings in real-time as you deploy optimizations</li>
              </ol>
            </div>
            <button
              onClick={initializeBaseline}
              disabled={isInitializing}
              className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-xs font-medium"
            >
              {isInitializing ? (
                <>
                  <FontAwesomeIcon icon={faSync} className="mr-1.5 animate-spin" />
                  Initializing...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} className="mr-1.5" />
                  Initialize Now
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { baselineSpend, baselineMonth, baselineCapturedAt, achievedSavings, savingsAchieved } = savingsData;

  // Calculate progress percentage: (achievedSavings / baselineSpend) × 100
  const progressPercent = baselineSpend > 0 ? (achievedSavings / baselineSpend) * 100 : 0;

  // Determine current level
  const getCurrentLevel = () => {
    if (progressPercent >= 85) return { level: 2, name: 'Elite Optimizer', icon: faCrown, color: 'text-purple-600', bgColor: 'bg-purple-600' };
    if (progressPercent >= 60) return { level: 1, name: 'Cost Saver', icon: faMedal, color: 'text-blue-600', bgColor: 'bg-blue-600' };
    return { level: 0, name: 'Getting Started', icon: faStar, color: 'text-gray-600', bgColor: 'bg-gray-600' };
  };

  const currentLevel = getCurrentLevel();

  // Format date - handles Firebase Timestamp objects
  const formatDate = (dateValue?: any) => {
    if (!dateValue) return 'N/A';
    try {
      let date: Date;

      // Handle Firebase Timestamp object (has _seconds field)
      if (dateValue._seconds !== undefined) {
        date = new Date(dateValue._seconds * 1000);
      }
      // Handle ISO string or milliseconds timestamp
      else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
        date = new Date(dateValue);
      }
      // Already a Date object
      else if (dateValue instanceof Date) {
        date = dateValue;
      }
      else {
        return 'N/A';
      }

      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'N/A';
    }
  };

  return (
    <div className="space-y-3 mb-4 font-mono">
      {/* Header */}
      <div className="bg-white border border-gray-300 rounded p-3">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-base font-bold text-gray-900 flex items-center">
              <FontAwesomeIcon icon={faTrophy} className="mr-2 text-sm" />
              Savings Tracker
            </h2>
            <p className="text-xs text-gray-600 mt-0.5">Track your journey to cloud cost optimization mastery</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={resetAndRecalculate}
              disabled={isResetting}
              className="px-3 py-1.5 bg-white border border-gray-300 text-gray-700 rounded text-xs font-medium hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              title="Reset and recalculate with new business rules"
            >
              <FontAwesomeIcon icon={faRotate} className={`mr-1.5 ${isResetting ? 'animate-spin' : ''}`} />
              {isResetting ? 'Resetting...' : 'Reset & Recalc'}
            </button>
            <button
              onClick={fetchSavingsData}
              className="px-3 py-1.5 bg-blue-600 text-white rounded text-xs font-medium hover:bg-blue-700 transition-colors"
            >
              <FontAwesomeIcon icon={faSync} className="mr-1.5" />
              Refresh
            </button>
          </div>
        </div>

        {/* Baseline and Achieved Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mt-2">
          {/* Baseline Spend */}
          <div className="bg-blue-50 border border-gray-300 rounded p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-gray-900">
                Baseline Monthly Spend{baselineMonth ? ` - ${baselineMonth}` : ''}
              </span>
              <FontAwesomeIcon icon={faDollarSign} className="text-gray-400 text-sm" />
            </div>
            <div className="text-lg font-bold text-gray-900 mb-1">${baselineSpend.toLocaleString()}</div>
            <div className="text-xs text-gray-600">
              Captured: {formatDate(baselineCapturedAt)}
            </div>
          </div>

          {/* Savings Achieved */}
          <div className="bg-green-50 border border-gray-300 rounded p-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-bold text-gray-900">Savings Achieved</span>
              <FontAwesomeIcon icon={faCheckCircle} className="text-gray-400 text-sm" />
            </div>
            <div className="text-lg font-bold text-gray-900 mb-1">${achievedSavings.toLocaleString()}</div>
            <div className="text-xs text-gray-600">
              {progressPercent.toFixed(1)}% of baseline spend
            </div>
          </div>
        </div>
      </div>

      {/* Progress Bar with Levels */}
      <div className="bg-white border border-gray-300 rounded p-3">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <FontAwesomeIcon icon={currentLevel.icon} className={`${currentLevel.color} text-sm`} />
            <div>
              <h3 className="text-sm font-bold text-gray-900">
                {currentLevel.name}
              </h3>
              <p className="text-xs text-gray-600">
                {currentLevel.level === 0 && 'Complete optimization milestones to level up'}
                {currentLevel.level === 1 && 'Continue optimizing to reach Elite status'}
                {currentLevel.level === 2 && 'Maximum optimization achieved'}
              </p>
            </div>
          </div>
          <div className="text-right px-3 py-1.5 bg-gray-50 rounded border border-gray-300">
            <div className="text-lg font-bold text-gray-900">{progressPercent.toFixed(1)}%</div>
            <div className="text-xs text-gray-500">Complete</div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="relative mb-4">
          {/* Background track */}
          <div className="bg-gray-100 rounded-full h-3 overflow-hidden relative border border-gray-200">
            {/* Progress fill */}
            <div
              className={`h-full transition-all duration-500 ${
                progressPercent >= 85 ? 'bg-green-500' :
                progressPercent >= 60 ? 'bg-blue-500' :
                'bg-gray-400'
              }`}
              style={{ width: `${Math.min(progressPercent, 90)}%` }}
            ></div>
          </div>

          {/* Milestone Markers */}
          <div className="flex justify-between mt-3 px-1">
            {/* Start - 0% */}
            <div className="flex flex-col items-start">
              <div className="text-xs font-medium text-gray-700">Start</div>
              <div className="text-xs text-gray-500">0%</div>
            </div>

            {/* Level 1 - 60% */}
            <div className="flex flex-col items-center">
              <div className={`flex items-center space-x-1 mb-0.5 ${progressPercent >= 60 ? 'text-blue-600' : 'text-gray-400'}`}>
                <FontAwesomeIcon icon={faMedal} className="text-xs" />
                <span className={`text-xs font-medium ${progressPercent >= 60 ? 'font-bold' : ''}`}>
                  Level 1
                </span>
              </div>
              <div className="text-xs text-gray-500">60%</div>
            </div>

            {/* Level 2 - 85% */}
            <div className="flex flex-col items-center">
              <div className={`flex items-center space-x-1 mb-0.5 ${progressPercent >= 85 ? 'text-green-600' : 'text-gray-400'}`}>
                <FontAwesomeIcon icon={faCrown} className="text-xs" />
                <span className={`text-xs font-medium ${progressPercent >= 85 ? 'font-bold' : ''}`}>
                  Level 2
                </span>
              </div>
              <div className="text-xs text-gray-500">85%</div>
            </div>

            {/* Max - 90% */}
            <div className="flex flex-col items-end">
              <div className="text-xs font-medium text-gray-700">Target</div>
              <div className="text-xs text-gray-500">90%</div>
            </div>
          </div>
        </div>

        {/* Next Milestone */}
        {currentLevel.level < 2 && (
          <div className="bg-gray-50 rounded p-2 border border-gray-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <div className="text-xs font-bold text-gray-900 mb-0.5">
                  Next Milestone: {currentLevel.level === 0 ? 'Cost Saver' : 'Elite Optimizer'}
                </div>
                <div className="text-xs text-gray-600">
                  ${((currentLevel.level === 0 ? baselineSpend * 0.6 : baselineSpend * 0.85) - achievedSavings).toLocaleString()} more to unlock
                </div>
              </div>
              <div className="text-right px-2 py-1 bg-white rounded border border-gray-300">
                <div className="text-sm font-bold text-gray-900">
                  {currentLevel.level === 0 ? '60%' : '85%'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Achievement Unlocked */}
        {currentLevel.level === 2 && (
          <div className="bg-green-50 rounded p-2 border border-green-300">
            <div className="flex items-center space-x-2">
              <FontAwesomeIcon icon={faCrown} className="text-green-600 text-sm" />
              <div>
                <div className="text-xs font-bold text-green-900">Elite Optimizer Status Achieved!</div>
                <div className="text-xs text-green-700">You've reached maximum cost optimization</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Achieved Savings Breakdown */}
      <div className="bg-white border border-gray-300 rounded overflow-hidden">
        <button
          onClick={() => setShowBreakdown(!showBreakdown)}
          className="w-full px-3 py-2 bg-gray-100 flex items-center justify-between hover:bg-gray-200 transition-colors"
        >
          <h3 className="text-xs font-bold text-gray-900">Savings Breakdown by Category</h3>
          <FontAwesomeIcon
            icon={showBreakdown ? faChevronUp : faChevronDown}
            className="text-gray-400 text-xs"
          />
        </button>

        {showBreakdown && (
          <div className="p-3">
            <div className="space-y-2">
              {Object.entries(savingsAchieved)
                .filter(([_, value]) => value > 0)
                .sort(([, a], [, b]) => b - a)
                .map(([category, amount]) => (
                  <div key={category} className={`flex items-center justify-between p-2 rounded border ${getCategoryColor(category)}`}>
                    <span className="text-xs font-medium">{getCategoryLabel(category)}</span>
                    <span className="text-xs font-bold">${amount.toLocaleString()}/mo</span>
                  </div>
                ))}
              {Object.values(savingsAchieved).every(v => v === 0) && (
                <div className="text-center py-4">
                  <FontAwesomeIcon icon={faInfoCircle} className="text-gray-300 text-base mb-2" />
                  <div className="text-xs text-gray-600">No savings achieved yet</div>
                  <div className="text-xs text-gray-500 mt-1">Deploy recommendations from the Compute tab to start saving!</div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
